import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import NavBar from '../components/NavBar';
import AddReminderForm from '../components/AddReminderForm';
import { usePets } from '../hooks';
import { useReminders } from '../hooks/useReminders';
import type { Reminder } from '../types';
import { formatDate } from '../utils/helpers';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function uuidToNotifId(uuid: string): number {
  return parseInt(uuid.replace(/-/g, '').slice(0, 8), 16);
}

function isOverdue(dueDateIso: string): boolean {
  return new Date(dueDateIso) < new Date();
}

// ─── Reminder Row ─────────────────────────────────────────────────────────────

function ReminderRow({
  reminder,
  onComplete,
  onDelete,
}: {
  reminder: Reminder;
  onComplete: (id: string, nid: number) => Promise<void>;
  onDelete: (id: string, nid: number) => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const notifId = uuidToNotifId(reminder.id);
  const overdue  = !reminder.is_completed && isOverdue(reminder.due_date);
  const dueAt    = new Date(reminder.due_date);

  const handle = async (fn: () => Promise<void>) => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };

  return (
    <article className={`glass-card p-4 flex items-start gap-3 animate-slide-up transition-opacity ${reminder.is_completed ? 'opacity-50' : ''}`}>
      {/* Checkbox */}
      <button
        id={`complete-reminder-${reminder.id}`}
        disabled={reminder.is_completed || busy}
        onClick={() => void handle(() => onComplete(reminder.id, notifId))}
        aria-label="Mark complete"
        className={`mt-0.5 w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all ${
          reminder.is_completed
            ? 'bg-brand-500 border-brand-500'
            : overdue
              ? 'border-red-400 hover:border-red-300'
              : 'border-white/30 hover:border-brand-400'
        }`}
      >
        {reminder.is_completed && <span className="text-white text-xs">✓</span>}
      </button>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p className={`font-medium text-sm leading-snug ${reminder.is_completed ? 'line-through text-white/40' : 'text-white'}`}>
          {reminder.title}
        </p>
        <p className={`text-xs mt-0.5 ${overdue ? 'text-red-400' : 'text-white/40'}`}>
          {overdue && !reminder.is_completed ? '⚠ Overdue · ' : ''}
          {formatDate(dueAt)}{' '}
          at{' '}
          {dueAt.toLocaleTimeString('en-IN', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
            timeZoneName: 'short',
          })}
        </p>
      </div>

      {/* Delete */}
      <button
        id={`delete-reminder-${reminder.id}`}
        disabled={busy}
        onClick={() => void handle(() => onDelete(reminder.id, notifId))}
        aria-label="Delete reminder"
        className="text-white/20 hover:text-red-400 transition-colors text-sm p-1"
      >
        🗑
      </button>
    </article>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function ReminderSkeleton() {
  return (
    <div className="glass-card p-4 flex gap-3 animate-pulse">
      <div className="w-5 h-5 rounded-full bg-white/10 mt-0.5" />
      <div className="flex-1 space-y-2">
        <div className="h-4 w-40 bg-white/10 rounded" />
        <div className="h-3 w-24 bg-white/10 rounded" />
      </div>
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyReminders({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="flex flex-col items-center text-center py-12 animate-fade-in">
      <div className="w-20 h-20 rounded-full bg-accent-500/10 border-2 border-dashed border-accent-500/30 flex items-center justify-center mb-4">
        <span className="text-3xl">🔔</span>
      </div>
      <h3 className="font-display text-xl font-bold text-white mb-2">No reminders yet</h3>
      <p className="text-white/40 text-sm max-w-xs mb-6">
        Set medication or vaccination reminders and we'll notify you right on time.
      </p>
      <button id="empty-add-reminder" onClick={onAdd} className="btn-primary">
        <span>+</span> Set first reminder
      </button>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

/**
 * RemindersPage — shows and manages all reminders for a single pet.
 * Accessible at /pets/:petId/reminders
 */
function RemindersPage() {
  const { petId } = useParams<{ petId: string }>();
  const navigate  = useNavigate();
  const { pets }  = usePets();
  const pet = pets.find((p) => p.id === petId);

  const { reminders, isLoading, error, addReminder, completeReminder, deleteReminder } =
    useReminders(petId ?? '');

  const [showForm, setShowForm] = useState(false);

  const pending   = reminders.filter((r) => !r.is_completed);
  const completed = reminders.filter((r) => r.is_completed);

  const handleAdd = async (payload: Parameters<typeof addReminder>[0]) => {
    await addReminder(payload);
    setShowForm(false);
  };

  return (
    <>
      <NavBar />

      <main className="page-container pt-20">
        {/* Back nav */}
        <button
          id="back-to-records"
          onClick={() => navigate(`/pets/${petId ?? ''}`)}
          className="flex items-center gap-1.5 text-sm text-white/50 hover:text-white mb-5 transition-colors group"
        >
          <span className="group-hover:-translate-x-1 transition-transform">←</span>
          Health records
        </button>

        {/* Pet header */}
        {pet && (
          <div className="mb-6">
            <h1 className="font-display text-2xl font-bold text-white">{pet.name}</h1>
            <p className="text-white/40 text-sm mt-0.5 capitalize">{pet.species} · {pet.breed}</p>
          </div>
        )}

        {/* Section header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-semibold text-white">Reminders</h2>
            {pending.length > 0 && (
              <p className="text-xs text-white/40 mt-0.5">{pending.length} pending</p>
            )}
          </div>
          {reminders.length > 0 && (
            <button id="add-reminder-btn" onClick={() => setShowForm(true)} className="btn-primary py-2 px-4 text-sm">
              <span>+</span> Add
            </button>
          )}
        </div>

        {/* States */}
        {isLoading && (
          <div className="space-y-3">{[1, 2].map((n) => <ReminderSkeleton key={n} />)}</div>
        )}
        {!isLoading && error && (
          <div role="alert" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-2xl px-5 py-4">
            {error}
          </div>
        )}
        {!isLoading && !error && reminders.length === 0 && (
          <EmptyReminders onAdd={() => setShowForm(true)} />
        )}

        {/* Pending reminders */}
        {pending.length > 0 && (
          <div className="space-y-3 mb-6">
            {pending.map((r) => (
              <ReminderRow
                key={r.id}
                reminder={r}
                onComplete={completeReminder}
                onDelete={deleteReminder}
              />
            ))}
          </div>
        )}

        {/* Completed reminders */}
        {completed.length > 0 && (
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-white/30 mb-3">Completed</p>
            <div className="space-y-3">
              {completed.map((r) => (
                <ReminderRow
                  key={r.id}
                  reminder={r}
                  onComplete={completeReminder}
                  onDelete={deleteReminder}
                />
              ))}
            </div>
          </div>
        )}
      </main>

      {showForm && petId && (
        <AddReminderForm
          petId={petId}
          onSubmit={(p) => handleAdd(p)}
          onClose={() => setShowForm(false)}
        />
      )}
    </>
  );
}

export default RemindersPage;
